import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ButtonsSection from "components/ui/buttons/ButtonsSection";
import  Header from "components/ui/header/Header"
import { VaulDrawerShell } from "components/ui/drawer/VaulDrawerShell";
import { useSubscriptionStatus } from "../home/hooks/useSubscriptionStatus";
import { useStripeSubscriptionStatus } from "../settings/hooks/useStripeSubscriptionStatus";
import { useSettingsContextData } from "../settings/hooks/useSettingsContextData";
import styles from "./AccountOverview.module.css";
import { UserService } from "../../services/UserService";
import { isDevEnvironment } from "../../env/runtimeEnv";
import {
  List,
  Images,
  LogOut,
  CircleX,
  ChevronRight,
  Palette,
  UserRound,
  CirclePlay,
  Lightbulb,
  Users,
  Bell,
  User,
} from "lucide-react";
import { accountOptions } from "./config/accountOptions";
import LeaveTeamModal from "../settings/components/LeaveTeamModal";
import DeleteDataModal from "../settings/components/DeleteDataModal";
import { useTeam } from "../../contexts/TeamContext";
import { TeamService } from "../../services/TeamService";
import DeleteOrganizationModal from "../settings/components/DeleteOrganizationModal";
import { OrganizationService } from "../../services/OrganizationService";

export default function SettingsPage() {
  const navigate = useNavigate();
  const {
    setMoods,
    isLoading,
    refreshData,
    setCauseTypes,
    organizationId,
    userId,
    email,
    logout,
  } = useSettingsContextData();
  const { organizationName } = useTeam();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteOrganizationModalOpen, setIsDeleteOrganizationModalOpen] =
    useState(false);
  const [isLeaveTeamModalOpen, setIsLeaveTeamModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLeavingTeam, setIsLeavingTeam] = useState(false);
  const [deletionProgress, setDeletionProgress] = useState({
    current: 0,
    total: 0,
    stage: "",
  });

  interface Option {
    link?: string;
    iconKey?: string;
    title?: string;
    state?: { type?: string; hasStripeSubscription?: boolean };
    forLead?: boolean;
    forDevenv?: boolean;
  }

  const { isLead, teamId, teamName, setTeamId } = useTeam();

  type SettingsOptionsListProps = {
    options: Option[];
    handleClick?: (option: Option) => void;
  };

  const SettingsOptionsList = ({
    options,
    handleClick,
  }: SettingsOptionsListProps) => {
    return (
      <div className="select-component__duration-section">
        {options.map((option, idx) => {
          const Icon = option.iconKey ? optionIcons[option.iconKey] : Images;

          return (
            <div key={idx} className={styles.optionRow}>
              <button
                className={`strength-button select-component__duration-button two-part-button ${styles.optionButton} ${styles["option-row"]}`}
                style={{
                  display: "flex",
                  justifyContent: "left",
                  alignItems: "center",
                }}
                onClick={() => {
                  if (handleClick) {
                    handleClick(option);
                    return;
                  }
                  if (option.link) {
                    navigate(
                      option.link,
                      option.state ? { state: option.state } : undefined
                    );
                  }
                }}
              >
                {Icon && (
                  <Icon
                    className={`w-4 h-4 text-red-500 ${styles.settingsIconSpacing}`}
                  />
                )}
                <span>{option.title}</span>
                <ChevronRight
                  className={`w-4 h-4 text-red-500 ${styles.optionIconChevron} ${styles["chevron-right"]}`}
                />
              </button>
            </div>
          );
        })}
      </div>
    );
  };
  const optionIcons: any = {
    list: List,
    images: Images,
    logIn: UserRound,
    delete: CircleX,
    account: User,
    logOut: LogOut,
    palette: Palette,
    bell: Bell,
    circlePlay: CirclePlay,
    lightbulb: Lightbulb,
    users: Users,
  };

  const handleLeaveTeam = async () => {
    setIsLeavingTeam(true);
    try {

      await TeamService.removeTeamMemberFromTeam(teamId, userId);
      setIsLeaveTeamModalOpen(false);

      setTeamId(null);
      navigate("/app/join-team", { replace: true });
    } catch (error) {
      console.error("❌ Error leaving team:", error);
      alert("Failed to leave team. Please try again.");
    } finally {
      setIsLeavingTeam(false);
    }
  };

  const handleDeleteAllData = async () => {
    setIsDeleting(true);
    try {
      await UserService.deleteAllUserData(
        userId,
        setMoods,
        setCauseTypes,
        refreshData,
        (
          progress: React.SetStateAction<{
            current: number;
            total: number;
            stage: string;
          }>
        ) => setDeletionProgress(progress)
      );

      setIsDeleteModalOpen(false);
      setDeletionProgress({ current: 0, total: 0, stage: "" });
      navigate("/app/home");
    } catch (error) {
      console.error("❌ Error deleting user data:", error);
      alert("Failed to delete data. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteOrganization = async () => {
    setIsDeleting(true);
    try {
      await OrganizationService.deleteOrganization(organizationId, (progress) =>
        setDeletionProgress(progress)
      );

      setMoods([]);
      setCauseTypes([]);

      setIsDeleteOrganizationModalOpen(false);
      setDeletionProgress({ current: 0, total: 0, stage: "" });

    } catch (error) {
      console.error("❌ Error deleting user data:", error);
      alert("Failed to delete data. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAccountOptionClick = (option: any) => {
    switch (option.function) {
      case "logOut":
        logout();
        return;
      case "showIntro":
        navigate("/app/intro");
        return;
      case "setIsLeaveTeamModalOpen":
        setIsLeaveTeamModalOpen(true);
        return;
      case "setIsDeleteModalOpen":
        setIsDeleteModalOpen(true);
        return;
      case "setIsDeleteOrganizationModalOpen":
        setIsDeleteOrganizationModalOpen(true);
        return;
      default:
        if (option.link) {
          navigate(
            option.link,
            option.state ? { state: option.state } : undefined
          );
        }
    }
  };

  const handleLeaveTeamDrawerChange = useCallback((nextOpen: boolean) => {
    setIsLeaveTeamModalOpen(nextOpen);
  }, []);

  const handleDeleteDrawerChange = useCallback((nextOpen: boolean) => {
    setIsDeleteModalOpen(nextOpen);
    if (!nextOpen) {
      setDeletionProgress({ current: 0, total: 0, stage: "" });
    }
  }, []);

  const handleDeleteOrganizationDrawerChange = useCallback(
    (nextOpen: boolean) => {
      setIsDeleteOrganizationModalOpen(nextOpen);
      if (!nextOpen) {
        setDeletionProgress({ current: 0, total: 0, stage: "" });
      }
    },
    []
  );

  const { hasActiveSubscription } = useSubscriptionStatus({ userId });
  const { hasStripeSubscription, isLoading: isStripeLoading } =
    useStripeSubscriptionStatus(userId);

  const visibleAvailableOptions = useMemo(
    () =>
      accountOptions
        .filter(
          (option) =>
            !(isDevEnvironment && option.forDevEnv === false) &&
            (!(option.forLead === true && isLead === false) ||
              (option.forLead && organizationName === userId)) &&
            (isLead === true || organizationName === userId) &&
            option.forLead !== false
        )
        .map((option) => option),
    [isDevEnvironment, hasStripeSubscription]
  );

  if (isLoading) {
    return (
      <div className={styles.settingsLoading}>
        <p>Loading user data...</p>
      </div>
    );
  }

  return (
    <div className="root">
      <Header title={"Account"} />
      <div>
        {email && (
          <div className={styles.settingsContainer}>
            <div className={styles.settingsEmailRow}>
              <p className={styles.settingsLink}>{email}</p>
            </div>
            {!isDevEnvironment &&
              email &&
              !hasActiveSubscription &&
              !organizationId && (
                <p
                  onClick={() => navigate("/app/upgrade")}
                  className={styles.settingsLink}
                >
                  Subscription
                </p>

              )}

            <p
                onClick={() => {
                  console.log("try logout")
                  logout()
                }}
                className={styles.settingsLink}
            >
              Log out
            </p>
          </div>
        )}
        {
          <SettingsOptionsList
            options={visibleAvailableOptions}
            handleClick={handleAccountOptionClick}
          />
        }

        <div className="buttons-wrapper">
          <ButtonsSection />
        </div>

        {isLeaveTeamModalOpen && (
          <VaulDrawerShell
            open={isLeaveTeamModalOpen}
            onOpenChange={handleLeaveTeamDrawerChange}
          >
            <LeaveTeamModal
              setIsLeaveTeamModalOpen={setIsLeaveTeamModalOpen}
              handleLeaveTeam={handleLeaveTeam}
              isLeavingTeam={isLeavingTeam}
              embedded={false}
            />
          </VaulDrawerShell>
        )}

        {isDeleteOrganizationModalOpen && (
          <VaulDrawerShell
            open={isDeleteOrganizationModalOpen}
            onOpenChange={handleDeleteOrganizationDrawerChange}
          >
            <DeleteOrganizationModal
              embedded
              setIsDeleteOrganizationModalOpen={
                setIsDeleteOrganizationModalOpen
              }
              isDeleting={isDeleting}
              deletionProgress={deletionProgress}
              setDeletionProgress={setDeletionProgress}
              handleDeleteAllData={handleDeleteOrganization}
            />
          </VaulDrawerShell>
        )}

        {isDeleteModalOpen && (
          <VaulDrawerShell
            open={isDeleteModalOpen}
            onOpenChange={handleDeleteDrawerChange}
          >
            <DeleteDataModal
              embedded
              setIsDeleteModalOpen={setIsDeleteModalOpen}
              isDeleting={isDeleting}
              deletionProgress={deletionProgress}
              setDeletionProgress={setDeletionProgress}
              handleDeleteAllData={handleDeleteAllData}
            />
          </VaulDrawerShell>
        )}
      </div>
    </div>
  );
}
